import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of, switchMap } from 'rxjs';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import {
  ArkUserType, SystemFeatureModel, PrimeDatatableParams, SystemSettingModel,
  ClientViewModel, SystemSettingTypeViewModel, SystemSettingViewModel, SystemSettingViewModelPrimeDatatableResults
} from '../../core/api/models';
import { ClientsApiService, SystemSettingsApiService } from '../../core/api/services';
import { Utils } from '../../core/utils';
import { AuthService } from '../../main/auth.service';
import { AlertService } from '../../shared/services/alert.service';

@Component({
  selector: 'app-system-settings',
  templateUrl: './system-settings.component.html',
  standalone: false
})
export class SystemSettingsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private listRequest = 0;
  filter: PrimeDatatableParams = { first: 0, rows: 25, sortField: 'SettingOrder', sortOrder: 1 };
  list?: SystemSettingViewModelPrimeDatatableResults;
  currentItem?: SystemSettingModel;
  types: SystemSettingTypeViewModel[] = [];
  features: SystemFeatureModel[] = [];
  featureID?: number;
  loadingList = false;
  loadingItem = false;
  loadingLookups = true;
  saving = false;
  clientVisible = false;
  clientSetting?: SystemSettingViewModel | SystemSettingModel;
  clients?: ClientViewModel[];
  clientID: number | null = null;
  loadingClients = false;
  clientSaving = false;
  form: FormGroup;

  constructor(
    private readonly api: SystemSettingsApiService,
    private readonly clientsApi: ClientsApiService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly alertService: AlertService,
    formBuilder: FormBuilder
  ) {
    this.form = formBuilder.group({
      Name: ['', [Validators.required, Validators.maxLength(128)]],
      Description: ['', Validators.maxLength(1000)],
      SystemSettingTypeID: [{ value: 0, disabled: true }],
      DefaultValue: [{ value: null, disabled: true }],
      SystemFeatureID: [0, [Validators.required, Validators.min(1)]],
      ShowOnCompanySettings: [false],
      ShowOnEnterpriseSettings: [false],
      SettingOrder: [0, [Validators.required, Validators.pattern(/^-?\d+$/)]],
      Required: [false],
      Archived: [false],
      Settings: ['', Validators.maxLength(4000)]
    });
  }

  ngOnInit(): void {
    this.authService.checkIfAutorized([ArkUserType.Support, ArkUserType.Admin]);
    const featureID = Number(this.route.snapshot.queryParamMap.get('FeatureID'));
    this.featureID = Number.isInteger(featureID) && featureID > 0 ? featureID : undefined;
    forkJoin({
      types: this.api.GetSystemSettingTypes(),
      features: this.api.GetSystemFeatures()
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: data => {
        this.types = data.types;
        this.features = data.features;
        this.loadingLookups = false;
      },
      error: error => {
        this.loadingLookups = false;
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
    this.route.paramMap.pipe(
      switchMap(params => {
        const id = Number(params.get('ID'));
        this.currentItem = undefined;
        this.loadingItem = id > 0;
        return id > 0 ? this.api.Get({ body: { id } }).pipe(
          catchError(error => {
            this.alertService.error(Utils.getAjaxError(error));
            return of(undefined);
          })
        ) : of(undefined);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(item => {
      this.currentItem = item;
      this.loadingItem = false;
      if (item) {
        this.form.reset(item);
      }
    });
  }

  loadTable(event?: TableLazyLoadEvent): void {
    if (event) {
      this.filter = Utils.normalizePrimeDatatableEvent(event);
    }
    if (this.featureID) {
      this.filter.filters = {
        ...this.filter.filters,
        SystemFeatureID: [{ MatchMode: 'equals', Value: this.featureID }]
      };
    }
    const request = ++this.listRequest;
    this.loadingList = true;
    this.api.List({ body: this.filter }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: result => {
        if (request !== this.listRequest) {
          return;
        }
        this.list = result;
        this.loadingList = false;
      },
      error: error => {
        if (request !== this.listRequest) {
          return;
        }
        this.loadingList = false;
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }

  clear(table: Table): void {
    this.featureID = undefined;
    this.router.navigate([], { relativeTo: this.route, queryParams: { FeatureID: null }, queryParamsHandling: 'merge' });
    table.clear();
  }

  cancel(): void {
    if (!this.saving) {
      this.router.navigate(['/system-settings']);
    }
  }

  get canEditClientSettings(): boolean {
    return this.authService.hasUserType(ArkUserType.Admin);
  }

  editClientSetting(item: SystemSettingViewModel | SystemSettingModel): void {
    if (!this.canEditClientSettings || !item.ID || item.Archived || this.clientSaving) return;
    this.clientSetting = item;
    this.clientID = null;
    this.clientVisible = true;
    if (this.clients || this.loadingClients) return;
    this.loadingClients = true;
    this.clientsApi.List({ body: {
      multiSortMeta: [{ Field: 'Active', Order: -1 }, { Field: 'SiteName', Order: 1 }]
    } }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: data => {
        this.clients = data.Result ?? [];
        this.loadingClients = false;
      },
      error: error => {
        this.loadingClients = false;
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }

  save(): void {
    if (!this.currentItem || this.saving) {
      return;
    }
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.alertService.error('Complete the required fields and check the field lengths and setting order.');
      return;
    }
    const submitted: SystemSettingModel = { ...this.currentItem, ...this.form.getRawValue() };
    this.saving = true;
    this.api.Save({ body: submitted }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: saved => {
        this.saving = false;
        if (this.currentItem?.ID === submitted.ID) {
          this.currentItem = saved;
          this.form.reset(saved);
        }
        this.alertService.success('System setting saved successfully.');
      },
      error: error => {
        this.saving = false;
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }
}
